import {
  addLineItems,
  checkout,
  getCart,
  onBeforeCheckout,
  updateCart,
  updateLineItem
} from '@/cart/cart-store'
import { Cart, LineItemInput, UpdateCartInput, UpdateLineItemInput } from '@/cart/types'
import GiftWithPurchase, { type CartService } from '@/gift-with-purchase/gift-with-purchase'
import { logError } from '@/utilities/log'
import { effect } from '@preact/signals'
import { ComponentData } from './types'
import Experiment from '@/feature-flags/experiment'
import { getLocalizedStrings } from '@/localization/localization-store'
import withRootRoute from '@/localization/with-root-route'

export type BuyStack17Props = {
  step?: number
  bundle?: number
  selectedSellingPlan?: number | null
  cart: CartHandler
  bundles: Bundle[]
  upsellModal?: boolean
}

export type Bundle = {
  minimum_quantity?: number | null
  gift_bundle_variant?: string | null
  require_variants?: number[] | null
}

type CartHandler = CartService & {
  getCart: typeof getCart
  addLineItems: typeof addLineItems
  updateLineItem: typeof updateLineItem
  checkout: typeof checkout
  updateCart: typeof updateCart
  onBeforeCheckout: typeof onBeforeCheckout
}

export default class BuyStack17 extends ComponentData {
  step: number
  bundle: number
  selectedSellingPlan: number | null
  cart: CartHandler
  private cartState: Cart | null = null
  bundles: Bundle[]
  pendingCheckout = false
  allowExpressCheckout = false
  upsellModal: 'open' | 'closed' | 'none'
  _gwp: GiftWithPurchase

  constructor(props: BuyStack17Props) {
    super()
    this.step = props.step ?? 1
    this.bundle = props.bundle ?? 0
    this.selectedSellingPlan = props.selectedSellingPlan ?? null
    this.cart = props.cart
    this.bundles = props.bundles
    this.upsellModal = props.upsellModal ? 'closed' : 'none'
    this._gwp = new GiftWithPurchase({
      cart: this.cart,
      minQuantity: this.bundles[this.bundle]?.minimum_quantity,
      variants: this.bundles[this.bundle]?.require_variants,
      giftId: Number(this.bundles[this.bundle]?.gift_bundle_variant?.split('/').pop())
    })
  }

  init() {
    const cb = async () => {
      await this.updateSellingPlan(this.selectedSellingPlan)
    }
    this.cart.onBeforeCheckout(cb)
    const gwp = this.getUpdatedGwp()
    gwp.listenToCart()
    effect(() => {
      this.allowExpressCheckout = gwp.allowCheckout && this.allowCheckout
    })
    effect(() => (this.cartState = this.cart.getCart()))

    // Parse URL params for step and bundle,
    // in case they happen to be different from the initial props
    // this is a workaround for some caching bugs discovered in Datadog browser test environments
    const urlParams = new URLSearchParams(window.location.search)
    this.step = Number(urlParams.get('step') ?? 1)
    this.bundle = Number(urlParams.get('bundle') ?? 0)
  }

  getUpdatedGwp(): GiftWithPurchase {
    const gwp = this._gwp
    gwp.setOptions({
      giftId: Number(this.bundles[this.bundle]?.gift_bundle_variant?.split('/').pop()),
      variants: this.bundles[this.bundle]?.require_variants,
      minQuantity: this.bundles[this.bundle]?.minimum_quantity
    })
    return gwp
  }

  get allowCheckout(): boolean {
    const bundle = this.bundles[this.qualifiedBundle]
    const cart = this.cartState
    if (!bundle || !cart) return false

    const minQty = bundle.minimum_quantity ?? 0
    const requireVariants = bundle.require_variants ?? []

    const totalQuantity = cart.items
      .filter(({ id }) => requireVariants.includes(id))
      .reduce((sum, { quantity }) => sum + quantity, 0)

    return totalQuantity >= minQty
  }

  get qualifiedBundle(): number {
    if (!this.cartState) return this.bundle

    return handleQualifiedBundle(this.cartState, this.bundles, this.bundle)
  }

  async setStep(value: number): Promise<void> {
    if (document.startViewTransition) {
      await document.startViewTransition?.().ready
    }

    this.step = value
    window.scrollTo({ top: 0 })
    pushParam('step', value.toString())
    await this.updateSellingPlan(this.selectedSellingPlan)
  }

  setBundle(value: number): void {
    this.bundle = value
    pushParam('bundle', value.toString())
    this.getUpdatedGwp()
    void this.updateSellingPlan(this.selectedSellingPlan)
  }

  beginCheckout() {
    if (this.upsellModal === 'closed') {
      this.upsellModal = 'open'
    } else {
      void this.checkout()
    }
  }

  async checkout() {
    try {
      this.pendingCheckout = true
      await this.updateSellingPlan(this.selectedSellingPlan)
      await this.cart.waitForSettle()
      const url = Experiment.getInstance().getFlag('precheckout_page')
        ? '/pages/precheckout'
        : '/checkout'
      const rootRoute = getLocalizedStrings().storeRootRoute
      await this.cart.checkout(withRootRoute(url, rootRoute))
      setTimeout(() => (this.pendingCheckout = false), 2000)
    } catch (error) {
      logError(error, { message: 'BS_17: Failed checkout' })
    }
  }

  async incrementQuantity(variantId: number, sellingPlanId?: number) {
    await this.cart.addLineItems([
      {
        id: variantId,
        quantity: 1,
        selling_plan: sellingPlanId
      }
    ])

    await this.updateSellingPlan(this.selectedSellingPlan)
  }

  async decrementQuantity(variantId: number) {
    if (!this.cartState) return

    const lineUpdates = handleDecrement(this.cartState, variantId)
    await Promise.all(lineUpdates.map(line => this.cart.updateLineItem(line)))

    await this.updateSellingPlan(this.selectedSellingPlan)
  }

  async updateSellingPlan(sellingPlanId: number | null) {
    this.selectedSellingPlan = sellingPlanId

    if (!this.cartState) return

    try {
      const updates = handleUpdateSellingPlan(this.cartState, this.bundles, sellingPlanId)

      if (updates.length === 0) return

      await Promise.all(updates.map(u => this.cart.updateLineItem(u)))
    } catch (error) {
      logError(error, { message: 'BS_17: Error udpating selling plan' })
    }
  }
}

export function handleDecrement(cart: Cart, variantId: number): UpdateLineItemInput[] {
  const variantIndex = cart.items.findIndex(item => item.id === variantId)

  const items: UpdateLineItemInput[] = []

  if (variantIndex > -1) {
    const item = cart.items[variantIndex]
    items.push({
      id: item.id.toString(),
      quantity: item.quantity - 1
    })
  }

  return items
}

export function handleAddGift(cart: Cart, bundle?: Bundle): LineItemInput | null {
  if (!bundle) return null

  const giftId = bundle.gift_bundle_variant?.split('/').pop()

  if (!giftId) return null

  const requireVariants = bundle.require_variants

  if (!requireVariants) return null

  const totalQuantity = cart.items
    .filter(({ id }) => requireVariants.includes(id))
    .reduce((sum, { quantity }) => sum + quantity, 0)

  const minQuantity = bundle.minimum_quantity ?? 0

  if (minQuantity > totalQuantity) return null

  return {
    id: Number(giftId),
    quantity: 1
  }
}

function getRequireVariantIds(bundles: Bundle[]): Set<number> {
  const variantIds = new Set<number>()

  for (const { require_variants } of bundles) {
    if (!require_variants) continue

    for (const id of require_variants) {
      variantIds.add(id)
    }
  }

  return variantIds
}

export function handleUpdateSellingPlan(
  cart: Cart,
  bundles: Bundle[],
  sellingPlanId: number | null
): UpdateLineItemInput[] {
  const items: UpdateLineItemInput[] = []

  const variantIds = getRequireVariantIds(bundles)

  for (const item of cart.items) {
    if (!variantIds.has(item.id)) {
      continue
    }

    const planId = item.selling_plan_allocation?.selling_plan.id

    if (!sellingPlanId && !planId) {
      continue
    }

    if (sellingPlanId === planId) {
      continue
    }

    items.push({
      id: item.id.toString(),
      selling_plan: sellingPlanId,
      quantity: item.quantity
    })
  }

  return items
}

export function handleQualifiedBundle(
  cart: Cart,
  bundles: Bundle[],
  selectedBundle: number
): number {
  const variantIds = getRequireVariantIds(bundles)

  const totalQty = cart.items
    .filter(({ id }) => variantIds.has(id))
    .reduce((sum, { quantity }) => sum + quantity, 0)

  const selectedMinQty = bundles[selectedBundle].minimum_quantity ?? 1

  let qualifiedIndex = selectedBundle
  let maxQualifiedMinQty = -1

  for (let i = 0; i < bundles.length; i++) {
    const minQty = bundles[i].minimum_quantity ?? 1
    if (minQty < selectedMinQty) continue
    if (totalQty >= minQty && minQty > maxQualifiedMinQty) {
      qualifiedIndex = i
      maxQualifiedMinQty = minQty
    }
  }

  return qualifiedIndex
}

/**
 * Inspect the cart and remove any existing bundle products.
 * This is to prevent customers from accidentally checking out with multiple gift bundles.
 * Updates may be applied using the `updateCart` cart store action
 * @param cart - the current cart state
 * @returns line item updates to remove any bundle items
 */
export function handleClearBundles(cart: Cart): UpdateCartInput | null {
  const updates = cart.items
    .filter(item => item.has_components)
    .map(item => [item.id.toString(), 0] as const)

  return updates.length
    ? {
        updates: Object.fromEntries(updates)
      }
    : null
}

function pushParam(k: string, v: string): void {
  const url = new URL(window.location.href)
  url.searchParams.set(k, v)
  history.pushState({}, '', url.toString())
}
